import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner } from 'flowbite-react';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth } from '../utils/api';
import { getAuthEndpoints } from '../config/api';
import { getValidationMessages } from '../utils/helper';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  // Pancake Access Token state
  const [pancakeToken, setPancakeToken] = useState('');
  const [oldPancakeToken, setOldPancakeToken] = useState('');
  const [hasPancakeToken, setHasPancakeToken] = useState(false);
  const [pancakeLoading, setPancakeLoading] = useState(false);
  const [pancakeMessage, setPancakeMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Mật khẩu hiện tại là bắt buộc';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'Mật khẩu mới là bắt buộc';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Mật khẩu mới phải có ít nhất 6 ký tự';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Xác nhận mật khẩu là bắt buộc';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Mật khẩu xác nhận không khớp';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'Mật khẩu mới phải khác mật khẩu hiện tại';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const authEndpoints = getAuthEndpoints();
      const response = await fetchWithAuth(authEndpoints.CHANGE_PASSWORD, {
        method: 'POST',
        body: JSON.stringify({
          old_password: formData.currentPassword,
          new_password: formData.newPassword,
          new_password_confirm: formData.confirmPassword,
        }),
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Đổi mật khẩu thành công!' });
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setErrors({});
      } else {
        setMessage({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            'Đổi mật khẩu thất bại',
        });
      }
    } catch (error) {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi đổi mật khẩu',
      });
      console.error('Change password error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Load current Pancake Access Token
  useEffect(() => {
    const loadPancakeToken = async () => {
      try {
        const response = await fetchWithAuth(
          '/system-configs/by-key/PANCAKE_ACCESS_TOKEN'
        );
        if (response.success && response.data && response.data.value) {
          setHasPancakeToken(true);
          setOldPancakeToken(response.data.value);
        } else {
          setHasPancakeToken(false);
          setOldPancakeToken('');
        }
      } catch (error) {
        console.error('Load pancake token error:', error);
      }
    };
    loadPancakeToken();
  }, []);

  // Handle Pancake Token update
  const handlePancakeTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pancakeToken.trim()) {
      setPancakeMessage({
        type: 'failure',
        text: 'Vui lòng nhập Pancake Access Token',
      });
      return;
    }

    setPancakeLoading(true);
    setPancakeMessage(null);

    try {
      const response = await fetchWithAuth(
        '/system-configs/by-key/PANCAKE_ACCESS_TOKEN',
        {
          method: 'PUT',
          body: JSON.stringify({
            value: pancakeToken.trim(),
          }),
        }
      );

      if (response.success) {
        setPancakeMessage({
          type: 'success',
          text: 'Cập nhật Pancake Access Token thành công!',
        });
      } else {
        setPancakeMessage({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            'Cập nhật Pancake Access Token thất bại',
        });
      }
    } catch (error) {
      setPancakeMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi cập nhật Pancake Access Token',
      });
      console.error('Update pancake token error:', error);
    } finally {
      setPancakeLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Cài đặt</h1>
        <p className="text-gray-600">Quản lý thông tin tài khoản và bảo mật</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pancake Access Token Card */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Pancake Access Token
            </h3>

            {pancakeMessage && (
              <Alert color={pancakeMessage.type} className="mb-4">
                <span>{pancakeMessage.text}</span>
              </Alert>
            )}

            <form onSubmit={handlePancakeTokenSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="pancakeToken"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Access Token
                </label>
                <input
                  type="text"
                  id="pancakeToken"
                  value={pancakeToken}
                  onChange={e => {
                    setPancakeToken(e.target.value);
                    if (pancakeMessage) {
                      setPancakeMessage(null);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nhập Pancake Access Token"
                />
                {hasPancakeToken && (
                  <p className="mt-1 text-xs text-green-500">
                    Token đã được thêm trước đó, bạn có thể xóa và thêm lại nếu
                    cần thiết. <br /> Token hiện tại: {oldPancakeToken}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Token này được sử dụng để kết nối với Pancake API
                </p>
              </div>

              <Button
                type="submit"
                disabled={pancakeLoading}
                className="w-full btn-primary"
              >
                {pancakeLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Đang xử lý...
                  </>
                ) : (
                  'Cập nhật Token'
                )}
              </Button>
            </form>
          </div>
        </Card>

        {/* Change Password Card */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Đổi mật khẩu
            </h3>

            {message && (
              <Alert color={message.type} className="mb-4">
                <span>{message.text}</span>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="currentPassword"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Mật khẩu hiện tại
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.currentPassword
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Nhập mật khẩu hiện tại"
                />
                {errors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.currentPassword}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Mật khẩu mới
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.newPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Nhập mật khẩu mới"
                />
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.newPassword}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Xác nhận mật khẩu mới
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Nhập lại mật khẩu mới"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Đang xử lý...
                  </>
                ) : (
                  'Đổi mật khẩu'
                )}
              </Button>
            </form>
          </div>
        </Card>

        {/* Account Info Card */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Thông tin tài khoản
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên đăng nhập
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                  <span className="text-gray-900">
                    {user?.username || 'N/A'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trạng thái tài khoản
                </label>
                <div className="px-3 py-2 bg-green-50 border border-green-300 rounded-md">
                  <span className="text-green-800 font-medium">Hoạt động</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quyền truy cập
                </label>
                <div className="px-3 py-2 bg-blue-50 border border-blue-300 rounded-md">
                  <span className="text-blue-800 font-medium">
                    Administrator
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Security Tips */}
      <Card className="mt-6">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Mẹo bảo mật
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Mật khẩu mạnh
                </p>
                <p className="text-sm text-gray-600">
                  Sử dụng ít nhất 8 ký tự bao gồm chữ hoa, chữ thường, số và ký
                  tự đặc biệt
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Đổi mật khẩu định kỳ
                </p>
                <p className="text-sm text-gray-600">
                  Nên đổi mật khẩu 3-6 tháng một lần để tăng cường bảo mật
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Không chia sẻ mật khẩu
                </p>
                <p className="text-sm text-gray-600">
                  Tuyệt đối không chia sẻ mật khẩu với người khác
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Đăng xuất khi không dùng
                </p>
                <p className="text-sm text-gray-600">
                  Luôn đăng xuất khỏi hệ thống khi hoàn thành công việc
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Settings;
